// src/components/ProtectedRoute.tsx
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { isApproverToken } from "../utils/authRole";
import Loading from "./Loading"; // ← use your existing loader

type Props = {
  children: ReactNode;
  /**
   * Restrict the route to one side of the preparer / approver split. Omitted
   * means "any signed-in user", which is right for everything both roles
   * share — the ECI workspace included, since an approver opens it to review
   * what a preparer built.
   */
  role?: "approver" | "preparer";
};

export default function ProtectedRoute({ children, role }: Props) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // 1️⃣ Prevent flicker during auth restore
  if (loading) {
    return <Loading />;
  }

  // 2️⃣ Block unauthenticated access
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  // 3️⃣ Send each role to its own dashboard.
  //
  // Without this, `/preparer` and `/approver` were both guarded by nothing but
  // "is signed in", so typing the other role's URL opened the other role's
  // dashboard — a preparer could sit in the approver's Case Listing.
  //
  // THIS IS NAVIGATION, NOT SECURITY. It stops the wrong screen from opening;
  // it cannot stop anything from being fetched, because the browser is the one
  // being asked. Every rule that actually matters — who may read an
  // engagement, who may sign one off — has to be enforced by the API, which
  // currently authenticates these routes without checking the role.
  if (role === "approver" && !isApproverToken()) {
    return <Navigate to="/preparer" replace />;
  }
  if (role === "preparer" && isApproverToken()) {
    return <Navigate to="/approver" replace />;
  }

  // 4️⃣ Allow access
  return <>{children}</>;
}
